import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
// IMPORT THÊM Backdrop và CircularProgress TỪ MUI
import { Backdrop, CircularProgress } from '@mui/material'; 

import DataListManager from './DataListManager'; 
import ReportFrame from './ReportFrame'; 
import CustomDialog from './Dialog';
import AuthContext from './auth/Context';

// IMPORT HÀM ADAPTER ĐÃ VIẾT Ở BƯỚC TRƯỚC
import { adaptLegacyReportConfig,parseDataListConfig } from "./DataDynamicPage";

const QuickActionContext = createContext({});
export const useQuickAction = () => useContext(QuickActionContext);

const PAGE_CONFIGS = new Map();
const REPORT_CONFIGS = new Map();

// ================= WRAPPER QUẢN LÝ VÒNG ĐỜI CỦA CUSTOM DIALOG =================
const ReportDialogWrapper = ({ config, onCloseContext }) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.open();
    }
  }, []);

  return (
    <CustomDialog
      ref={dialogRef}
      fullScreen
      onClose={onCloseContext} 
      TransitionProps={{ onExited: onCloseContext }} 
      PaperProps={{ sx: { bgcolor: 'background.default', borderRadius: 0 } }}
    >
      <ReportFrame
        apiCode={config.apiCode}
        code={config.code}
        title={config.title}
        filterFields={config.filterFields}
        displayFields={config.displayFields}
        defaultCondition={config.defaultCondition}
        drillDownCondition={config.drillDownCondition}
        onBack={() => {
          if (dialogRef.current) dialogRef.current.close();
        }} 
        {...(config.extraProps || {})}
      />
    </CustomDialog>
  );
};

// ================= XEM CHI TIẾT MỘT BẢN GHI (openView) — HỘP THOẠI TOÀN MÀN HÌNH, XẾP CHỒNG =================
// Trước đây openView đặt `config` rồi vẽ DataListManager headless NGAY SAU nội dung trang (không có hộp thoại, không mang
// cấu hình trang): bản xem rơi xuống cuối trang — người dùng bấm tên lead / cơ hội / chiến dịch trong một trang chi tiết mà
// "không thấy gì", và nếu có thấy thì cũng không phải giao diện tuỳ biến (customViewScript) của màn hình đó.
// Nay mỗi lần xem là MỘT hộp thoại toàn màn hình mang đủ cấu hình trang (customViewScript, viewTabs, apiCode…); xem tiếp từ
// bên trong (chiến dịch -> lead -> cơ hội) thì hộp thoại mới chồng lên, đóng thì quay về đúng chỗ cũ.
const ViewDialogWrapper = ({ view, onCloseView, onEditItem }) => {
  const dialogRef = useRef(null);
  useEffect(() => {
    if (dialogRef.current) dialogRef.current.open();
  }, []);
  const dong = () => { if (dialogRef.current) dialogRef.current.close(); else onCloseView(); };
  // Giữ nguyên identity giữa các lần vẽ: DataListManager chạy lại effect mở bản xem mỗi khi headlessConfig đổi.
  const headlessConfig = React.useMemo(() => ({ action: 'view', code: view.code, fields: view.fields, title: view.title, id: view.id }),
    [view.code, view.fields, view.title, view.id]);
  const pc = view.pageConfig || {};
  return (
    <CustomDialog
      ref={dialogRef}
      fullScreen
      onClose={onCloseView}
      TransitionProps={{ onExited: onCloseView }}
      PaperProps={{ sx: { bgcolor: 'background.default', borderRadius: 0 } }}
    >
      <DataListManager
        {...(view.pageConfig || {})}
        key={view.lan}
        headless={true}
        headlessConfig={headlessConfig}
        onHeadlessClose={dong}
        onEdit={(item) => onEditItem(view, item)}
        code={view.code}
        apiCode={pc.apiCode || undefined}
        title={view.title}
        fields={view.fields}
        canAdd={pc.canAdd !== false} canEdit={pc.canEdit !== false} canDelete={pc.canDelete !== false}
      />
    </CustomDialog>
  );
};

// ================= PROVIDER CHÍNH =================
export const QuickActionProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [callback, setCallback] = useState(null);
  // Các bản ghi đang XEM (openView), xếp chồng: phần tử cuối nằm trên cùng.
  const [views, setViews] = useState([]);
  const dongView = (key) => setViews((ds) => ds.filter((v) => v.key !== key));

  const authContext = useContext(AuthContext); 
  const getPageConfig = async (code) => {
    let config = PAGE_CONFIGS.get(code);
    if(config) return config;
    config=   authContext?.config?.getPageConfig ? authContext.config.getPageConfig(code) : null;
    //Lấy config từ server
    if(!config){
      const userToken = authContext?.userInfo?.token || (typeof authContext?.getToken === 'function' ? authContext.getToken() : '');
      if(userToken){
        const res = await authContext.apis.asyncGetList(userToken, "data_list_config", { condition: { moduleCode: code }, limit: 1 });
        config = res?.data?.[0] || res?.[0];
        if(config){
          config = parseDataListConfig(config);
        }
      }
    }
    //Cache page config
    if(config) PAGE_CONFIGS.set(code,config);
    //
    return config;
  };

  const openAdd = async (code, fields, title, prefillData = {}, onSuccess,onCancel) => {
    if(code==="currency") code ="dmnt";
    const pageConfig = await getPageConfig(code);
    if (pageConfig || fields?.length>0) { 
      setConfig({ 
        action: 'add', 
        code, 
        fields: (fields && fields.length > 0) ? fields : (pageConfig?.fields || []), 
        title: title || pageConfig?.title || 'Thêm mới', 
        prefillData, 
        onSuccess,
        onCancel,
        pageConfig
      });
    } else {
        authContext.quickAdd(code,prefillData,onSuccess,onCancel);
    }
  };

  const openEdit = async (code, fields, title, itemData, onSuccess,onCancel, extraFields) => {
    if(code==="currency") code ="dmnt";

    const pageConfig = await getPageConfig(code);
    if(pageConfig || fields?.length>0 || extraFields?.length>0){
      // fields cơ sở: ưu tiên fields truyền vào, nếu không lấy từ cấu hình (data_list_config)
      const baseFields = (fields && fields.length > 0) ? fields : (pageConfig?.fields || []);
      // extraFields: fields bổ sung được nối thêm (vd fields "Giá trị mặc định" của form Tuỳ chọn)
      const mergedFields = (extraFields && extraFields.length > 0) ? [...baseFields, ...extraFields] : baseFields;
      setConfig({
        action: 'edit',
        code,
        fields: mergedFields,
        title: title || pageConfig?.title || 'Cập nhật',
        itemData,
        onSuccess,
        onCancel
      });
    }else{
      authContext.quickEdit(code,itemData,onSuccess,onCancel);
    }

  };

  const openView = async (code, fields, title, id) => {
    if(code==="currency") code ="dmnt";

    const pageConfig = await getPageConfig(code);
    if(pageConfig || fields?.length>0){
      const key = 'v' + Date.now() + Math.random().toString(36).slice(2, 7);
      setViews((ds) => [...ds, {
        key, lan: 0,
        code,
        fields: (fields && fields.length > 0) ? fields : (pageConfig?.fields || []),
        title: title || pageConfig?.title || 'Chi tiết',
        id,
        pageConfig,
      }]);
    }else{
      authContext.objectView(code,id,{fullScreen:true});
    }
    
  };

  // ============================================================================
  //THÀNH HÀM ASYNC ĐỂ FETCH API VÀ CHẠY ADAPTER
  // ============================================================================
  const openReport = async (code, title, filterFields, displayFields, defaultCondition = {}, options = {}) => {
    const {callback,...extraProps} = options;
    //Không hiểu callback được gọi ngay tại đây mà không chờ đến khi close report
  
    setConfig({ action: 'loading' });
    setCallback(() => callback);

    try {
      // 1. Kéo cấu hình gốc từ Local Registry
      let reportConfig = REPORT_CONFIGS.get(code);
      if(!reportConfig){
        reportConfig =  authContext?.config?.getReportConfig ? authContext.config.getReportConfig(code) : null;
        // 2. Nếu Local không có -> Lấy từ Server
        if (!reportConfig) {
          const userToken = authContext?.userInfo?.token || (typeof authContext?.getToken === 'function' ? authContext.getToken() : '');
          if(userToken){
            const res = await authContext.apis.asyncGetList(userToken, "reportinfo", { condition: { code }, limit: 1 });
            const item = res?.data?.[0] || res?.[0];
            //console.log("[quickaction item]",item);
            if (item) {
                // Nếu là config cũ -> Chạy qua Adapter (Nhớ truyền kèm authContext để mock API)
                if (item.columns && !item.displayFields) {
                  reportConfig = await adaptLegacyReportConfig(item, authContext);
                } else {
                  reportConfig = item;
                }
            }
          }
        }
        //Cache report config
        if(reportConfig) REPORT_CONFIGS.set(code,reportConfig);
      }
      if(!reportConfig) return;
      //console.log("[quickaction reportConfig]",reportConfig);

      // 3. Trộn cấu hình (Ưu tiên tham số truyền vào -> Cấu hình lấy được -> Mặc định)
      setConfig({ 
        action: 'report', 
        code, 
        apiCode:reportConfig?.apiCode,
        title: title || reportConfig?.title || "Báo cáo", 
        filterFields: (filterFields && filterFields.length > 0) ? filterFields : (reportConfig?.filterFields || []), 
        displayFields: (displayFields && displayFields.length > 0) ? displayFields : (reportConfig?.displayFields || reportConfig?.columns || []), 
        // Trộn điều kiện lấy từ config và tham số truyền vào (để giữ drill-down variables)
        defaultCondition: { ...(reportConfig?.defaultCondition || {}) }, 
        drillDownCondition:{...defaultCondition},
        extraProps: {
          processData: reportConfig?.processData,
          enableLoadMore: reportConfig?.enableLoadMore,
          renderCardItem: reportConfig?.renderCardItem,
          renderTableRow: reportConfig?.renderTableRow,
          // Đảm bảo truyền xuống đủ các param cho Script Drilldown
          drillDownCode: reportConfig?.drillDownCode,
          drillDownScript: reportConfig?.drillDownScript || reportConfig?.drilldown_info, 
          ...extraProps
        }
      });

    } catch (error) {
       console.error("Lỗi khi mở báo cáo:", error);
       setConfig(null); // Tắt loading nếu lỗi
       if (authContext?.alert) authContext.alert("Không thể tải cấu hình báo cáo!", null, "error");
    }
  };
  const close = () => {
    if(callback && typeof callback=="function" ){
      console.log("[QuickAction] close",config);
      callback();
    } 
    setConfig(null);
    setCallback(null);
  };
  //console.log("[QuickAction]",config);
  //console.log("[QuickAction] config",config);
  return (
    <QuickActionContext.Provider value={{ openAdd, openEdit, openView, openReport, close }}>
      {children}
      
      {/* HIỆU ỨNG CHỜ: Khóa màn hình lại khi đang lấy cấu hình API */}
      <Backdrop 
         open={config?.action === 'loading'} 
         sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      
      {/* 1. RENDER DATALIST DÀNH CHO ADD/EDIT/VIEW */}
      {config && ['add', 'edit', 'view'].includes(config.action) && (
        <DataListManager
          {...(config.pageConfig||{})}
          headless={true} 
          headlessConfig={config}
          onHeadlessClose={close}
          code={config.code}
          apiCode={config.apiCode}
          title={config.title}
          fields={config.fields}
          canAdd={true} canEdit={true} canDelete={true}
        />
      )}

      {/* 1b. XEM CHI TIẾT: mỗi bản ghi một hộp thoại toàn màn hình. Bấm "Sửa" trong bản xem -> mở form (openEdit) chồng lên;
          lưu xong vẽ lại bản xem (đổi `lan` -> nạp lại bản ghi). */}
      {views.map((v) => (
        <ViewDialogWrapper key={v.key} view={v} onCloseView={() => dongView(v.key)}
          onEditItem={(view, item) => setConfig({
            // Form SỬA mang cấu hình trang (customFormScript…) như khi sửa từ danh sách; lưu xong vẽ lại bản xem.
            action: 'edit', code: view.code, fields: view.fields, title: view.title, itemData: item, pageConfig: view.pageConfig,
            onSuccess: () => setViews((ds) => ds.map((x) => (x.key === view.key ? { ...x, lan: x.lan + 1 } : x))),
          })} />
      ))}

      {/* 2. RENDER REPORT THÔNG QUA CUSTOM DIALOG WRAPPER */}
      {config && config.action === 'report' && (
        <ReportDialogWrapper config={config} onCloseContext={close} />
      )}
    </QuickActionContext.Provider>
  );
};

export default QuickActionContext;